import { useState, useEffect, useMemo } from "react";

/**
 * Hook for widgets to get their effective settings with preview values applied
 * @param {string} widgetId - The widget's ID
 * @param {object} actualSettings - The actual settings from the database
 * @returns {object} - The effective settings (actual + preview)
 */
export function useWidgetPreview(widgetId, actualSettings) {
  const [previewSettings, setPreviewSettings] = useState({});

  useEffect(() => {
    // Function to handle preview updates
    const handlePreviewUpdate = (event) => {
      const { widgetId: eventWidgetId, key, value } = event.detail;

      if (eventWidgetId === widgetId) {
        setPreviewSettings((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    };

    // Listen for preview updates
    window.addEventListener("widgetPreviewUpdate", handlePreviewUpdate);

    // Initialize with any existing preview settings
    if (
      typeof window !== "undefined" &&
      window.widgetPreviewSettings?.[widgetId]
    ) {
      setPreviewSettings(window.widgetPreviewSettings[widgetId]);
    }

    return () => {
      window.removeEventListener("widgetPreviewUpdate", handlePreviewUpdate);
    };
  }, [widgetId]);

  // Combine actual settings with preview settings
  const effectiveSettings = useMemo(() => {
    return { ...actualSettings, ...previewSettings };
  }, [actualSettings, previewSettings]);

  return effectiveSettings;
}
